import { PlusOutlined } from '@ant-design/icons';
import { Row, Col, Space, Spin } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Form,
  FormContent,
  message,
  SubContent,
  Table,
  Tag,
  Upload,
  Wrapper,
  useBreadcrumbRoutes,
} from '@maxtropy/components';
import { useNavigate } from 'react-router-dom';
import {
  apiV2HighEnergyDeviceDeviceTreePost,
  apiV2PaintingEnergyConfigDetailPost,
  apiV2PaintingEnergyConfigSavePost,
  V2HighEnergyDeviceDeviceTreePostResponse,
  V2PaintingEnergyConfigDetailPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import EnergyUnitSelectModal from './components/EnergyUnitSelectModal';
import HighEnergyDeviceModal, { DeviceTreeNode } from './components/HighEnergyDeviceModal';
import styles from './index.module.scss';
import AddClassificationModal from './components/AddClassificationModal';

type DeviceTreeData = Exclude<V2HighEnergyDeviceDeviceTreePostResponse['list'], undefined>[number];
type DetailResponse = V2PaintingEnergyConfigDetailPostResponse;
type EnergyMediumConfig = {
  workshop?: number;
  electricityEnergyUnitId?: number;
  gasEnergyUnitId?: number;
  airEnergyUnitId?: number;
  electricityGroupId?: number;
  electricityGroupName?: string;
  gasGroupId?: number;
  gasGroupName?: string;
  airGroupId?: number;
  airGroupName?: string;
};
type CrossAreaConfig = {
  workshop?: number;
  zoneCode?: string;
  groupId?: number;
  groupName?: string;
  energyUnitIds?: number[];
};
type HighEnergyDeviceConfig = {
  workshop?: number;
  inventoryIds?: number[];
};
type ElectricityClassification = {
  name?: string;
  groupId?: number;
  groupName?: string;
  energyUnitIds?: number[];
};
type DetailEnergyUnit = {
  energyUnitId?: number;
  energyUnitName?: string;
  groupId?: number;
  groupName?: string;
};

const WORKSHOPS = [
  { value: 1, label: '涂装一期' },
  { value: 2, label: '涂装二期' },
  { value: 3, label: '涂装三期' },
];

const ENERGY_FIELDS = [
  { key: 'electricityEnergyUnitId', label: '用电统计' },
  { key: 'gasEnergyUnitId', label: '天然气统计' },
  { key: 'airEnergyUnitId', label: '压缩空气统计' },
] as const;

const ZONE_CONFIGS: Record<number, { code: string; label: string }[]> = {
  1: ['A', 'B', 'C', 'D', 'E', 'F'].map(code => ({ code, label: `${code}跨能配置` })),
  2: ['A', 'B', 'C', 'D', 'E', 'F'].map(code => ({ code, label: `${code}跨能配置` })),
  3: ['AB', 'CD'].map(code => ({ code, label: `${code}跨能配置` })),
};

const transformDeviceTree = (tree?: DeviceTreeData[]): DeviceTreeNode[] =>
  (tree ?? []).map(item => ({
    key: `type-${item.deviceTypeId}`,
    title: item.deviceTypeName,
    value: `type-${item.deviceTypeId}`,
    children: (item.deviceLedgerInfo ?? []).map(device => ({
      key: String(device.id),
      title: device.assetDeviceName,
      value: String(device.id),
    })),
  }));

const flattenDeviceLabels = (tree: DeviceTreeNode[]) => {
  const labels = new Map<number, string>();
  tree.forEach(item => {
    item.children?.forEach(child => labels.set(Number(child.value), child.title ?? String(child.value)));
  });
  return labels;
};

const normalizeNumberArray = (value?: number[]) => (value ?? []).filter(item => item !== undefined && item !== null);

const collectDetailUnitMeta = (
  detail: DetailResponse
): { labels: Map<number, string>; groupByUnitId: Map<number, number> } => {
  const labels = new Map<number, string>();
  const groupByUnitId = new Map<number, number>();
  const append = (units?: DetailEnergyUnit[]) => {
    units?.forEach(unit => {
      if (!unit.energyUnitId) return;
      if (unit.energyUnitName) {
        labels.set(unit.energyUnitId, unit.energyUnitName);
      }
      if (unit.groupId) {
        groupByUnitId.set(unit.energyUnitId, unit.groupId);
      }
    });
  };

  detail.energyMediumConfigs?.forEach(item => {
    append([item.electricityEnergyUnit, item.gasEnergyUnit, item.airEnergyUnit].filter(Boolean) as DetailEnergyUnit[]);
  });
  detail.crossAreaConfigs?.forEach(item => append(item.energyUnits as DetailEnergyUnit[] | undefined));
  detail.electricityClassifications?.forEach(item => append(item.energyUnits as DetailEnergyUnit[] | undefined));

  return { labels, groupByUnitId };
};

const normalizeDetailEnergyMediumConfigs = (detail: DetailResponse): EnergyMediumConfig[] =>
  (detail.energyMediumConfigs ?? []).map(item => ({
    workshop: item.workshop,
    electricityEnergyUnitId: item.electricityEnergyUnit?.energyUnitId,
    electricityGroupId: item.electricityGroupId,
    electricityGroupName: item.electricityGroupName,
    gasEnergyUnitId: item.gasEnergyUnit?.energyUnitId,
    gasGroupId: item.gasGroupId,
    gasGroupName: item.gasGroupName,
    airEnergyUnitId: item.airEnergyUnit?.energyUnitId,
    airGroupId: item.airGroupId,
    airGroupName: item.airGroupName,
  }));

const normalizeDetailCrossAreaConfigs = (detail: DetailResponse): CrossAreaConfig[] =>
  (detail.crossAreaConfigs ?? []).map(item => ({
    workshop: item.workshop,
    zoneCode: item.zoneCode,
    groupId: item.groupId,
    groupName: item.groupName,
    energyUnitIds: item.energyUnits?.map(unit => unit.energyUnitId).filter(Boolean) as number[] | undefined,
  }));

const normalizeDetailHighEnergyConfigs = (detail: DetailResponse): HighEnergyDeviceConfig[] =>
  (detail.highEnergyDeviceConfigs ?? []).map(item => ({
    workshop: item.workshop,
    inventoryIds: item.inventoryList?.map(device => device.inventoryId).filter(Boolean) as number[] | undefined,
  }));

const normalizeDetailElectricityClassifications = (detail: DetailResponse): ElectricityClassification[] =>
  (detail.electricityClassifications ?? []).map(item => ({
    name: item.name,
    groupId: item.groupId,
    groupName: item.groupName,
    energyUnitIds: item.energyUnits?.map(unit => unit.energyUnitId).filter(Boolean) as number[] | undefined,
  }));

const PaintingWorkshopOverviewConfig: React.FC = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const navigate = useNavigate();
  const [form] = Form.useForm<{ fileKey?: string }>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [unitLabels, setUnitLabels] = useState<Map<number, string>>(new Map());
  const [unitGroupByUnitId, setUnitGroupByUnitId] = useState<Map<number, number>>(new Map());
  const [deviceTree, setDeviceTree] = useState<DeviceTreeNode[]>([]);
  const [deviceLabels, setDeviceLabels] = useState<Map<number, string>>(new Map());
  const [energyMediumConfigs, setEnergyMediumConfigs] = useState<EnergyMediumConfig[]>([]);
  const [crossAreaConfigs, setCrossAreaConfigs] = useState<CrossAreaConfig[]>([]);
  const [highEnergyDeviceConfigs, setHighEnergyDeviceConfigs] = useState<HighEnergyDeviceConfig[]>([]);
  const [electricityClassifications, setElectricityClassifications] = useState<ElectricityClassification[]>([]);
  const [unitModal, setUnitModal] = useState<{
    open: boolean;
    multiple?: boolean;
    value?: number[];
    onOk?: (value: number[]) => void;
  }>({ open: false });
  const [deviceModal, setDeviceModal] = useState<{
    open: boolean;
    value?: number[];
    onOk?: (value: number[]) => void;
  }>({ open: false });
  const [classificationModal, setClassificationModal] = useState<{ open: boolean; index?: number }>({ open: false });

  const fetchDetail = useCallback((options?: { silent?: boolean; errorText?: string }) => {
    if (!options?.silent) {
      setLoading(true);
    }
    return apiV2PaintingEnergyConfigDetailPost({})
      .then(detail => {
        const { labels, groupByUnitId } = collectDetailUnitMeta(detail);
        setUnitLabels(labels);
        setUnitGroupByUnitId(groupByUnitId);
        setEnergyMediumConfigs(normalizeDetailEnergyMediumConfigs(detail));
        setCrossAreaConfigs(normalizeDetailCrossAreaConfigs(detail));
        setHighEnergyDeviceConfigs(normalizeDetailHighEnergyConfigs(detail));
        setElectricityClassifications(normalizeDetailElectricityClassifications(detail));
      })
      .catch(() => {
        message.error(options?.errorText ?? '查询配置失败，请稍后重试');
      })
      .finally(() => {
        if (!options?.silent) {
          setLoading(false);
        }
      });
  }, []);

  useEffect(() => {
    fetchDetail();

    apiV2HighEnergyDeviceDeviceTreePost({})
      .then(deviceRes => {
        const nextDeviceTree = transformDeviceTree(deviceRes.list);
        setDeviceTree(nextDeviceTree);
        setDeviceLabels(flattenDeviceLabels(nextDeviceTree));
      })
      .catch(() => {
        setDeviceTree([]);
        setDeviceLabels(new Map());
      });
  }, [fetchDetail]);

  const getUnitName = (id?: number) => (id ? unitLabels.get(id) ?? String(id) : undefined);
  const getDeviceName = (id?: number) => (id ? deviceLabels.get(id) ?? String(id) : undefined);

  const getEnergyConfig = (workshop: number) =>
    energyMediumConfigs.find(item => item.workshop === workshop) ?? { workshop };

  const updateEnergyConfig = (
    workshop: number,
    fieldKey: 'electricityEnergyUnitId' | 'gasEnergyUnitId' | 'airEnergyUnitId',
    value?: number
  ) => {
    setEnergyMediumConfigs(prev => {
      const exists = prev.some(item => item.workshop === workshop);
      if (exists) {
        return prev.map(item => (item.workshop === workshop ? { ...item, [fieldKey]: value } : item));
      }
      return [...prev, { workshop, [fieldKey]: value }];
    });
  };

  const getCrossConfig = (workshop: number, zoneCode: string) =>
    crossAreaConfigs.find(item => item.workshop === workshop && item.zoneCode === zoneCode) ?? { workshop, zoneCode };

  const updateCrossConfig = (workshop: number, zoneCode: string, ids: number[]) => {
    setCrossAreaConfigs(prev => {
      const exists = prev.some(item => item.workshop === workshop && item.zoneCode === zoneCode);
      if (exists) {
        return prev.map(item =>
          item.workshop === workshop && item.zoneCode === zoneCode ? { ...item, energyUnitIds: ids } : item
        );
      }
      return [...prev, { workshop, zoneCode, energyUnitIds: ids }];
    });
  };

  const getHighEnergyConfig = (workshop: number) =>
    highEnergyDeviceConfigs.find(item => item.workshop === workshop) ?? { workshop };

  const updateHighEnergyConfig = (workshop: number, ids: number[]) => {
    setHighEnergyDeviceConfigs(prev => {
      const exists = prev.some(item => item.workshop === workshop);
      if (exists) {
        return prev.map(item => (item.workshop === workshop ? { ...item, inventoryIds: ids } : item));
      }
      return [...prev, { workshop, inventoryIds: ids }];
    });
  };

  const updateClassificationUnits = (index: number, ids: number[]) => {
    setElectricityClassifications(prev =>
      prev.map((item, itemIndex) => (itemIndex === index ? { ...item, energyUnitIds: ids } : item))
    );
  };

  const updateClassificationName = (index: number, name: string) => {
    setElectricityClassifications(prev =>
      prev.map((item, itemIndex) => (itemIndex === index ? { ...item, name } : item))
    );
  };

  const mergeUnitLabels = useCallback((labels: Map<number, string>) => {
    setUnitLabels(prev => new Map([...prev, ...labels]));
  }, []);

  const openUnitModal = (value: number[], onOk: (value: number[]) => void, multiple = false) => {
    setUnitModal({ open: true, value, onOk, multiple });
  };

  const openDeviceModal = (value: number[], onOk: (value: number[]) => void) => {
    setDeviceModal({ open: true, value, onOk });
  };

  const renderUnitTags = (ids?: number[], onClose?: (id: number) => void) =>
    normalizeNumberArray(ids).map(id => (
      <Tag key={id} closable={isEdit && !!onClose} onClose={() => onClose?.(id)}>
        {getUnitName(id)}
      </Tag>
    ));

  const renderDeviceTags = (ids?: number[], onClose?: (id: number) => void) =>
    normalizeNumberArray(ids).map(id => (
      <Tag key={id} closable={isEdit && !!onClose} onClose={() => onClose?.(id)}>
        {getDeviceName(id)}
      </Tag>
    ));

  const handleSave = () => {
    const names = electricityClassifications.map(item => item.name?.trim()).filter(Boolean) as string[];
    if (new Set(names).size !== names.length) {
      message.error('分类名称已存在，请勿重复输入');
      return;
    }

    setSaving(true);
    const fileKey = form.getFieldValue('fileKey');
    apiV2PaintingEnergyConfigSavePost({
      timestamp: Date.now(),
      energyMediumConfigs: energyMediumConfigs.map(item => ({
        workshop: item.workshop,
        electricityEnergyUnitId: item.electricityEnergyUnitId,
        gasEnergyUnitId: item.gasEnergyUnitId,
        airEnergyUnitId: item.airEnergyUnitId,
      })),
      crossAreaConfigs: crossAreaConfigs.map(item => ({
        workshop: item.workshop,
        zoneCode: item.zoneCode,
        energyUnitIds: normalizeNumberArray(item.energyUnitIds),
      })),
      highEnergyDeviceConfigs: highEnergyDeviceConfigs.map(item => ({
        workshop: item.workshop,
        inventoryIds: normalizeNumberArray(item.inventoryIds),
      })),
      electricityClassifications: electricityClassifications.map(item => ({
        name: item.name,
        energyUnitIds: normalizeNumberArray(item.energyUnitIds),
      })),
      fileKey,
    })
      .then(() => {
        message.success('保存成功');
        setIsEdit(false);
        fetchDetail({ silent: true, errorText: '保存成功，但刷新配置失败，请手动刷新' });
      })
      .catch(() => {
        message.error('保存失败，请稍后重试');
      })
      .finally(() => setSaving(false));
  };

  const columns = [
    {
      title: '分类名称',
      dataIndex: 'name',
      width: 220,
    },
    {
      title: '用能单元',
      dataIndex: 'energyUnitIds',
      render: (_: unknown, record: ElectricityClassification, index: number) => (
        <div className={styles.tags}>
          <Button
            type="link"
            className={styles.linkButton}
            disabled={!isEdit}
            onClick={() =>
              openUnitModal(
                normalizeNumberArray(record.energyUnitIds),
                ids => updateClassificationUnits(index, ids),
                true
              )
            }
          >
            配置
          </Button>
          {renderUnitTags(record.energyUnitIds, unitId =>
            updateClassificationUnits(
              index,
              normalizeNumberArray(record.energyUnitIds).filter(id => id !== unitId)
            )
          )}
        </div>
      ),
    },
    {
      title: '操作',
      dataIndex: 'operation',
      width: 120,
      render: (_: unknown, record: ElectricityClassification, index: number) => (
        <Space size={8}>
          <Button
            type="link"
            className={styles.linkButton}
            disabled={!isEdit}
            onClick={() => setClassificationModal({ open: true, index })}
          >
            编辑
          </Button>
          <Button
            type="link"
            className={styles.linkButton}
            disabled={!isEdit}
            onClick={() => setElectricityClassifications(prev => prev.filter((_, itemIndex) => itemIndex !== index))}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? []), { name: '配置' }]} className={styles.wrapper}>
      <Spin spinning={loading}>
        <FormContent title="涂装车间概览配置" narrow>
          {WORKSHOPS.map(workshop => {
            const energyConfig = getEnergyConfig(workshop.value);
            const highEnergyConfig = getHighEnergyConfig(workshop.value);
            return (
              <SubContent title={workshop.label} key={workshop.value}>
                <div className={styles.section}>
                  <div className={styles.sectionTitle}>总能配置</div>
                  <div className={styles.sectionBody}>
                    <Row gutter={[24, 12]}>
                      {ENERGY_FIELDS.map(field => {
                        const id = energyConfig[field.key];
                        return (
                          <Col span={8} key={field.key}>
                            <div className={styles.configItem}>
                              <span className={styles.label}>{field.label}：</span>
                              <div className={styles.tags}>
                                <Button
                                  type="link"
                                  className={styles.linkButton}
                                  disabled={!isEdit}
                                  onClick={() =>
                                    openUnitModal(
                                      id ? [id] : [],
                                      ids => updateEnergyConfig(workshop.value, field.key, ids[0]),
                                      false
                                    )
                                  }
                                >
                                  配置
                                </Button>
                                {id && (
                                  <Tag
                                    closable={isEdit}
                                    onClose={() => updateEnergyConfig(workshop.value, field.key, undefined)}
                                  >
                                    {getUnitName(id)}
                                  </Tag>
                                )}
                              </div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                </div>

                <div className={styles.section}>
                  <div className={styles.sectionTitle}>跨域配置</div>
                  <div className={styles.sectionBody}>
                    <Row gutter={[24, 12]}>
                      {(ZONE_CONFIGS[workshop.value] ?? []).map(zone => {
                        const config = getCrossConfig(workshop.value, zone.code);
                        return (
                          <Col span={12} key={zone.code}>
                            <div className={styles.configItem}>
                              <span className={styles.label}>{zone.label}：</span>
                              <div className={styles.tags}>
                                <Button
                                  type="link"
                                  className={styles.linkButton}
                                  disabled={!isEdit}
                                  onClick={() =>
                                    openUnitModal(
                                      normalizeNumberArray(config.energyUnitIds),
                                      ids => updateCrossConfig(workshop.value, zone.code, ids),
                                      true
                                    )
                                  }
                                >
                                  配置
                                </Button>
                                {renderUnitTags(config.energyUnitIds, unitId =>
                                  updateCrossConfig(
                                    workshop.value,
                                    zone.code,
                                    normalizeNumberArray(config.energyUnitIds).filter(id => id !== unitId)
                                  )
                                )}
                              </div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                </div>

                <div className={styles.configItem}>
                  <span className={styles.label}>高能耗配置：</span>
                  <div className={styles.tags}>
                    <Button
                      type="link"
                      className={styles.linkButton}
                      disabled={!isEdit}
                      onClick={() =>
                        openDeviceModal(normalizeNumberArray(highEnergyConfig.inventoryIds), ids =>
                          updateHighEnergyConfig(workshop.value, ids)
                        )
                      }
                    >
                      配置
                    </Button>
                    {renderDeviceTags(highEnergyConfig.inventoryIds, deviceId =>
                      updateHighEnergyConfig(
                        workshop.value,
                        normalizeNumberArray(highEnergyConfig.inventoryIds).filter(id => id !== deviceId)
                      )
                    )}
                  </div>
                </div>
              </SubContent>
            );
          })}

          <SubContent title="用电分类配置">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={!isEdit}
              onClick={() => setClassificationModal({ open: true })}
            >
              新增分类
            </Button>
            <Table
              style={{ marginTop: 12 }}
              rowKey={(_: ElectricityClassification, index?: number) => `classification-${index}`}
              columns={columns}
              dataSource={electricityClassifications}
              pagination={false}
            />
          </SubContent>

          <SubContent title="涂装用能计划">
            <Form form={form}>
              <div className={styles.costPlan}>
                <Button type="link" disabled={!isEdit} href="/api/v2/painting/energy-cost-plan/export">
                  下载
                </Button>
                <Form.Item name="fileKey" noStyle>
                  <Upload listType="text" accept={['.xlsx']} uploadText="上传" fileSize={50} disabled={!isEdit}>
                    <Button type="link" disabled={!isEdit}>
                      上传
                    </Button>
                  </Upload>
                </Form.Item>
              </div>
            </Form>
          </SubContent>
          <Space className="sticky-footer" size={8}>
            {isEdit ? (
              <>
                <Button type="primary" loading={saving} onClick={handleSave}>
                  保存
                </Button>
                <Button onClick={() => setIsEdit(false)}>取消</Button>
              </>
            ) : (
              <>
                <Button type="primary" onClick={() => setIsEdit(true)}>
                  编辑
                </Button>
                <Button onClick={() => navigate('/painting/workshop/overview')}>返回</Button>
              </>
            )}
          </Space>
        </FormContent>
      </Spin>

      <EnergyUnitSelectModal
        open={unitModal.open}
        multiple={unitModal.multiple}
        value={unitModal.value}
        unitGroup={[]}
        unitGroupByUnitId={unitGroupByUnitId}
        onUnitLabelsChange={mergeUnitLabels}
        onCancel={() => setUnitModal({ open: false })}
        onOk={value => {
          unitModal.onOk?.(value);
          setUnitModal({ open: false });
        }}
      />
      <HighEnergyDeviceModal
        open={deviceModal.open}
        value={deviceModal.value}
        treeData={deviceTree}
        onCancel={() => setDeviceModal({ open: false })}
        onOk={value => {
          deviceModal.onOk?.(value);
          setDeviceModal({ open: false });
        }}
      />
      <AddClassificationModal
        open={classificationModal.open}
        title={classificationModal.index === undefined ? '新增分类' : '编辑分类'}
        initialName={
          classificationModal.index === undefined
            ? undefined
            : electricityClassifications[classificationModal.index]?.name
        }
        names={
          electricityClassifications
            .filter((_, index) => index !== classificationModal.index)
            .map(item => item.name?.trim())
            .filter(Boolean) as string[]
        }
        onCancel={() => setClassificationModal({ open: false })}
        onOk={name => {
          if (classificationModal.index === undefined) {
            setElectricityClassifications(prev => [...prev, { name, energyUnitIds: [] }]);
          } else {
            updateClassificationName(classificationModal.index, name);
          }
          setClassificationModal({ open: false });
        }}
      />
    </Wrapper>
  );
};

export default PaintingWorkshopOverviewConfig;
